import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useMessages } from '../i18n/locale';
import {
  EMPTY,
  etaDeltaMinutes,
  formatMinutes,
  formatTime,
  initials,
  minutesUntil,
} from '../lib/format';
import { statusLabel, toneForOrder, toneVars, type Tone } from '../lib/status';
import { useDismiss } from '../lib/use-dismiss';

/**
 * Библиотека компонентов кабинета (разд. 14 спецификации).
 *
 * Собственные, на токенах: ADR-0026. Каждый закрывает одну задачу экрана и знает про
 * состояния «пусто / загрузка / ошибка», потому что в рабочем месте эти состояния —
 * не исключение, а половина рабочего дня.
 *
 * Ни одной подписи внутри: строки приходят из ресурсов локали (FR-557).
 */

export function StatusChip({
  status,
  goodsLocation,
  atRisk = false,
}: {
  readonly status: string;
  readonly goodsLocation?: string | null | undefined;
  readonly atRisk?: boolean | undefined;
}): ReactElement {
  const messages = useMessages();
  const tone = toneForOrder(status, goodsLocation);
  const vars = toneVars(tone);

  return (
    <span style={{ display: 'inline-flex', gap: 'var(--sp-1)', alignItems: 'center' }}>
      <span
        style={{
          ...chipStyle,
          color: vars.color,
          background: vars.background,
        }}
      >
        <i aria-hidden style={dotStyle} />
        {statusLabel(messages, status)}
      </span>

      {/*
        Риск — рамка и отдельная подпись, а не замена цвета статуса: заказ может быть
        одновременно «в пути» и «под угрозой», и диспетчер должен видеть оба факта.
      */}
      {atRisk ? (
        <span
          title={messages.status.atRisk}
          style={{
            ...chipStyle,
            color: 'var(--warning)',
            background: 'var(--warning-soft)',
          }}
        >
          {messages.status.atRisk}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Номер заказа: сквозной номер группы крупно, номер TMS мельче (FR-587.2).
 *
 * Оба копируются в один клик: диспетчер диктует их по телефону и вставляет в поиск
 * CT, и «выделить мышью» на такой частоте — потерянные минуты за смену.
 */
export function OrderNumber({
  externalNumber,
  displayId,
}: {
  readonly externalNumber: string | null | undefined;
  readonly displayId: string;
}): ReactElement {
  const copy = (value: string): void => {
    void navigator.clipboard?.writeText(value);
  };

  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <button
        type="button"
        onClick={() => copy(externalNumber ?? displayId)}
        className="num"
        style={linkButtonStyle}
      >
        {externalNumber ?? displayId}
      </button>

      {externalNumber === null || externalNumber === undefined ? null : (
        <button
          type="button"
          onClick={() => copy(displayId)}
          className="num"
          style={{ ...linkButtonStyle, color: 'var(--text-faint)', fontSize: 'var(--fs-caption)' }}
        >
          {displayId}
        </button>
      )}
    </div>
  );
}

/** Маршрут двумя строками: откуда → куда. */
export function RouteCell({
  from,
  to,
}: {
  readonly from: string | null | undefined;
  readonly to: string;
}): ReactElement {
  return (
    <div style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
      <span style={ellipsis}>{from ?? EMPTY}</span>
      <span style={{ ...ellipsis, color: 'var(--text-muted)' }}>{to}</span>
    </div>
  );
}

/**
 * Дедлайн с обратным отсчётом.
 *
 * Красным — когда до дедлайна меньше порога: диспетчер сканирует колонку, и «21:42»
 * само по себе ни о чём не говорит, пока не сопоставишь с часами.
 */
export function DeadlineCell({
  deadlineAt,
  alertMinutes = 15,
  now = new Date(),
}: {
  readonly deadlineAt: string | null | undefined;
  readonly alertMinutes?: number | undefined;
  readonly now?: Date;
}): ReactElement {
  const messages = useMessages();
  const left = minutesUntil(deadlineAt, now);
  const urgent = left !== null && left <= alertMinutes;

  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <span className="num">{formatTime(deadlineAt)}</span>
      {left === null ? null : (
        <span
          className="num"
          style={{
            fontSize: 'var(--fs-caption)',
            color: urgent ? 'var(--danger)' : 'var(--text-muted)',
          }}
        >
          {formatMinutes(left, {
            minutes: messages.common.minutesShort,
            hours: messages.common.hoursShort,
          })}
        </span>
      )}
    </div>
  );
}

/** Прогноз и отклонение от дедлайна со знаком (разд. 4.3). */
export function EtaDelta({
  etaAt,
  deadlineAt,
}: {
  readonly etaAt: string | null | undefined;
  readonly deadlineAt: string | null | undefined;
}): ReactElement {
  const messages = useMessages();
  const delta = etaDeltaMinutes(etaAt, deadlineAt);

  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <span className="num">{formatTime(etaAt)}</span>
      {delta === null ? null : (
        <span
          className="num"
          style={{
            fontSize: 'var(--fs-caption)',
            color: delta > 0 ? 'var(--danger)' : 'var(--ok)',
          }}
        >
          {`${delta > 0 ? '+' : ''}${formatMinutes(delta, { minutes: messages.common.minutesShort, hours: messages.common.hoursShort })}`}
        </span>
      )}
    </div>
  );
}

/** Водитель: инициалы и фамилия. Пусто — «не назначен» курсивом. */
export function DriverChip({
  name,
  stale = false,
}: {
  readonly name: string | null | undefined;
  readonly stale?: boolean | undefined;
}): ReactElement {
  const messages = useMessages();

  if (name === null || name === undefined || name === '') {
    return (
      <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>
        {messages.cabinet.flow.noDriver}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
      <span
        aria-hidden
        style={{
          width: 'var(--badge-icon)',
          height: 'var(--badge-icon)',
          borderRadius: 'var(--r-pill)',
          display: 'grid',
          placeItems: 'center',
          fontSize: 'var(--fs-caption)',
          color: stale ? 'var(--danger)' : 'var(--text-muted)',
          background: stale ? 'var(--danger-weak)' : 'var(--surface-sunken)',
        }}
      >
        {initials(name)}
      </span>
      <span style={ellipsis}>{name}</span>
    </span>
  );
}

/**
 * Строка «должен был / приехал / забрал» (разд. 5).
 *
 * Самое ценное, что есть в кабинете Noor: по ней разбирается спор о времени. Наше
 * дополнение — раскраска отклонения: приехал раньше плана `ok`, позже порога `warn`.
 */
export function PlanFactRow({
  label,
  planned,
  actual,
  toleranceMinutes = 15,
}: {
  readonly label: string;
  readonly planned: string | null | undefined;
  readonly actual: string | null | undefined;
  readonly toleranceMinutes?: number | undefined;
}): ReactElement {
  const delta = etaDeltaMinutes(actual, planned);
  const tone: Tone = delta === null ? 'neutral' : delta > toleranceMinutes ? 'warn' : 'ok';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 'var(--sp-3)' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="num">{formatTime(planned)}</span>
      <span className="num" style={{ color: toneVars(tone).color }}>
        {formatTime(actual)}
      </span>
    </div>
  );
}

/**
 * Фильтры-пилюли со счётчиками (разд. 4.2), как в панели доставки: выбранная залита
 * акцентом, счётчик — в кружке. Тревожный фильтр с ненулевым счётчиком подсвечивает
 * кружок цветом тона, чтобы проблема была видна до клика.
 */
export function FilterChips({
  items,
  active,
  onSelect,
}: {
  /** `count` не задан — пилюля без счётчика (категории, у которых число не считается). */
  readonly items: readonly { code: string; label: string; count?: number; tone?: Tone }[];
  readonly active: string;
  readonly onSelect: (code: string) => void;
}): ReactElement {
  return (
    <div role="group" style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
      {items.map((item) => {
        const selected = item.code === active;
        const tone = item.tone ?? 'neutral';
        const alarm = !selected && (item.count ?? 0) > 0 && tone !== 'neutral';

        return (
          <button
            key={item.code}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(item.code)}
            style={{
              ...pillStyle,
              // Пилюля проблем с ненулевым счётчиком красная целиком: её надо заметить
              // с другого конца экрана, а не по кружку с числом.
              background: selected
                ? 'var(--accent)'
                : alarm
                  ? toneVars(tone).background
                  : 'var(--surface)',
              borderColor: selected
                ? 'var(--accent)'
                : alarm
                  ? toneVars(tone).color
                  : 'var(--line)',
              color: selected
                ? 'var(--text-on-accent)'
                : alarm
                  ? toneVars(tone).color
                  : 'var(--text-2)',
              // Нулевая пилюля бледнее, но не исчезает: пропажа ломает привычку искать
              // её на том же месте.
              opacity: !selected && item.count === 0 ? 'var(--muted-opacity)' : 1,
            }}
          >
            {item.label}
            {item.count === undefined ? null : (
              <b
                className="num"
                style={{
                  ...pillCountStyle,
                  background: selected
                    ? 'var(--accent-hover)'
                    : alarm
                      ? toneVars(tone).color
                      : 'var(--surface-muted)',
                  color: selected || alarm ? 'var(--text-on-accent)' : 'var(--text-2)',
                }}
              >
                {item.count}
              </b>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Состояние соединения и возраст данных (разд. 3). */
export function LiveBadge({
  state,
  updatedSecondsAgo,
}: {
  readonly state: 'connected' | 'reconnecting' | 'polling';
  readonly updatedSecondsAgo: number | null;
}): ReactElement {
  const messages = useMessages();
  const tone: Tone = state === 'connected' ? 'ok' : state === 'polling' ? 'warn' : 'danger';

  return (
    <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
      <span
        aria-hidden
        style={{
          width: 'var(--dot)',
          height: 'var(--dot)',
          borderRadius: 'var(--r-pill)',
          background: toneVars(tone).color,
        }}
      />
      <span style={{ color: 'var(--text-muted)' }}>{messages.cabinet.live[state]}</span>
      {updatedSecondsAgo === null ? null : (
        <span className="num" style={{ color: 'var(--text-faint)' }}>
          {`${messages.cabinet.live.updatedAgo} ${updatedSecondsAgo}`}
        </span>
      )}
    </span>
  );
}

/**
 * Пустое состояние.
 *
 * Различает «ничего нет» и «ничего не найдено по фильтру»: во втором случае даётся
 * сброс фильтров, иначе диспетчер решит, что заказов нет вовсе (разд. 15).
 */
export function EmptyState({
  text,
  onReset,
  resetLabel,
}: {
  readonly text: string;
  readonly onReset?: (() => void) | undefined;
  readonly resetLabel?: string | undefined;
}): ReactElement {
  return (
    <div
      style={{
        padding: 'var(--sp-10) var(--sp-6)',
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontSize: 'var(--fs-sm)',
        display: 'grid',
        gap: 'var(--sp-3)',
        justifyItems: 'center',
      }}
    >
      <span>{text}</span>
      {onReset === undefined ? null : (
        <button type="button" onClick={onReset} style={secondaryButtonStyle}>
          {resetLabel}
        </button>
      )}
    </div>
  );
}

/** Правая панель поверх таблицы: разбор идёт в контексте очереди, а не вместо неё. */
export function Drawer({
  title,
  onClose,
  children,
  actions,
}: {
  readonly title: ReactNode;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly actions?: ReactNode | undefined;
}): ReactElement {
  const messages = useMessages();

  // Esc закрывает карточку, но не из-под открытого окна подтверждения: там Esc —
  // это «отмена» окна, а не потеря карточки.
  useDismiss(true, () => {
    if (document.querySelector('[aria-modal="true"]') === null) {
      onClose();
    }
  });

  return (
    <aside
      role="dialog"
      aria-label={typeof title === 'string' ? title : undefined}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(var(--drawer-w), 92vw)',
        background: 'var(--bg)',
        boxShadow: 'var(--shadow-pop)',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        gridTemplateColumns: 'minmax(0, 1fr)',
        zIndex: 20,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-4) var(--sp-6)',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', minWidth: 0 }}>
          {title}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          {actions}
          <button
            type="button"
            onClick={onClose}
            aria-label={messages.cabinet.common.close}
            style={secondaryButtonStyle}
          >
            {messages.cabinet.common.close}
          </button>
        </div>
      </header>

      <div
        style={{
          overflow: 'auto',
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 'var(--sp-3)',
          alignContent: 'start',
        }}
      >
        {children}
      </div>
    </aside>
  );
}

/**
 * Подтверждение необратимого действия (разд. 15).
 *
 * Текст называет, что именно произойдёт, — никаких «Вы уверены?». Где требуется
 * причина, поле обязательно: отмена без причины не разбирается ни CT, ни отчётом.
 */
export function ConfirmDialog({
  title,
  description,
  requireReason = false,
  reason,
  onReason,
  onConfirm,
  onCancel,
}: {
  readonly title: string;
  readonly description: string;
  readonly requireReason?: boolean | undefined;
  readonly reason?: string | undefined;
  readonly onReason?: ((value: string) => void) | undefined;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  const messages = useMessages();

  useDismiss(true, onCancel);
  const blocked = requireReason && (reason ?? '').trim() === '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 30,
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--sp-6)',
          width: 'min(460px, 92vw)',
          display: 'grid',
          gap: 'var(--sp-3)',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <h2>{title}</h2>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>{description}</p>

        {requireReason ? (
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={{ color: 'var(--text-muted)' }}>
              {messages.cabinet.common.reasonRequired}
            </span>
            <input value={reason ?? ''} onChange={(event) => onReason?.(event.target.value)} />
          </label>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button type="button" onClick={onConfirm} disabled={blocked} style={dangerButtonStyle}>
            {messages.cabinet.common.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Card({
  children,
  tone,
}: {
  readonly children: ReactNode;
  readonly tone?: Tone | undefined;
}): ReactElement {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        // Тон — полоска слева, как у строки таблицы: цвет не заливает всю карточку.
        boxShadow:
          tone === undefined
            ? 'var(--shadow-card)'
            : `inset var(--rail-w) 0 0 var(--${tone}), var(--shadow-card)`,
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-4)',
        display: 'grid',
        gap: 'var(--sp-3)',
      }}
    >
      {children}
    </section>
  );
}

export function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: ReactNode;
}): ReactElement {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <span className="caption" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
}

/**
 * Кнопки — как в панели доставки: компактные, со скруглением, с рамкой. Залитая
 * акцентом — главная, одна на экран или панель.
 */
const buttonBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--sp-2)',
  height: 'var(--btn-h)',
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--line-strong)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-semibold)',
  whiteSpace: 'nowrap',
  transition: 'background var(--motion), color var(--motion)',
};

export const primaryButtonStyle: CSSProperties = {
  ...buttonBase,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
  color: 'var(--text-on-accent)',
};

export const secondaryButtonStyle: CSSProperties = {
  ...buttonBase,
  background: 'var(--surface)',
  color: 'var(--text-2)',
};

/** Разрушительное действие: белая с красной рамкой — не спорит с главной кнопкой. */
export const dangerButtonStyle: CSSProperties = {
  ...buttonBase,
  background: 'var(--surface)',
  borderColor: 'var(--danger-line)',
  color: 'var(--danger)',
};

export const smallButtonStyle: CSSProperties = {
  ...secondaryButtonStyle,
  height: 'var(--btn-h-sm)',
  padding: '0 var(--sp-2)',
  fontSize: 'var(--fs-xs)',
};

/** Метка состояния: прямоугольник со скруглением и точкой цвета тона. */
const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--sp-1)',
  height: 'var(--tag-h)',
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--r-xs)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 'var(--fw-bold)',
  whiteSpace: 'nowrap',
};

const dotStyle: CSSProperties = {
  width: 'var(--dot)',
  height: 'var(--dot)',
  borderRadius: 'var(--r-pill)',
  background: 'currentColor',
  flex: 'none',
};

export const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  height: 'var(--pill-h)',
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--line)',
  // Фон и цвет — из темы: без них кнопка берёт белый фон браузера и в тёмной теме
  // светится пятном.
  background: 'var(--surface)',
  color: 'var(--text-2)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-semibold)',
  whiteSpace: 'nowrap',
  transition: 'background var(--motion), color var(--motion)',
};

const pillCountStyle: CSSProperties = {
  minWidth: 'var(--dot-count)',
  height: 'var(--dot-count)',
  padding: '0 var(--sp-1)',
  borderRadius: 'var(--r-pill)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 'var(--fw-bold)',
  display: 'grid',
  placeItems: 'center',
};

const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'left',
  color: 'var(--text)',
};

const ellipsis: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
